/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';
import { RouteRow, RouteRowGroup, groupRoutesByPrefix } from '../utils/routeModel';
import { MultiChipCell } from './MultiChipCell';

interface NeighborDetailPanelProps {
  neighborIp: string;
  /** The selected neighbor's route rows (already filtered to match the active filters). */
  rows: RouteRow[];
  direction: 'INBOUND' | 'OUTBOUND';
  neighborInfo?: { peerAs?: number; description?: string; status?: string };
  onClose: () => void;
  /** Opens the deeper per-prefix modal for a clicked row. */
  onPrefixClick?: (prefixData: any, neighborIp: string) => void;
}

/** Distinct, non-empty values in encounter order — one chip per distinct value. */
const distinctValues = (values: (string | null | undefined)[]): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value)));

/**
 * Docked right-side panel showing a selected neighbor's announced prefixes at route-table
 * grain (PREFIX / LP / MED / AS_PATH / NEXT HOP / RT), replacing the old neighbor modal. Opened by
 * clicking a compact neighbor node in the topology. Best paths are highlighted, withdrawn
 * prefixes struck through; clicking a row opens the deeper per-prefix detail.
 */
export const NeighborDetailPanel: React.FC<NeighborDetailPanelProps> = ({
  neighborIp,
  rows,
  direction,
  neighborInfo,
  onClose,
  onPrefixClick
}) => {
  const distinctPrefixes = useMemo(
    () => new Set(rows.map((r) => `${r.prefix}|${r.family}`)).size,
    [rows]
  );

  // One visual row per prefix; its route tables are collapsed into a deduped list (CTX-73).
  const groups = useMemo(() => groupRoutesByPrefix(rows), [rows]);

  // Deduped destinations (route tables inbound / subnets outbound) for a grouped prefix.
  const destinationsFor = (group: RouteRowGroup): string[] =>
    distinctValues(
      group.rows.map((r) => (direction === 'OUTBOUND' ? r.subnetId || r.routeTableId : r.routeTableId))
    );

  // Resolved next hops (ENI / TGW) for a grouped prefix. A prefix reaching several route tables
  // can be installed via a different ENI in each, so this is a list, not a single value.
  const nextHopsFor = (group: RouteRowGroup): string[] => distinctValues(group.rows.map((r) => r.nextHop));

  const destinationHeader = direction === 'OUTBOUND' ? 'Subnet' : 'RT';
  const status = neighborInfo?.status || 'UNKNOWN';
  const statusVariant = status.toUpperCase() === 'ESTABLISHED' ? 'success' : 'default';

  const handleRowClick = (row: RouteRow) => {
    if (!onPrefixClick) return;
    // Aggregate distinct vpc/rt for this prefix across the neighbor's rows for the detail modal.
    const samePrefix = rows.filter((r) => r.prefix === row.prefix && r.family === row.family);
    onPrefixClick(
      {
        prefix: row.prefix,
        family: row.family,
        local_pref: row.localPref,
        med: row.med,
        as_path: row.asPath,
        vpcCount: new Set(samePrefix.map((r) => r.vpc)).size,
        rtCount: new Set(samePrefix.map((r) => r.routeTableId)).size
      },
      neighborIp
    );
  };

  // Panel width was raised from 520px to fit the Next Hop column: a full `eni-0123456789abcdef0`
  // is 21 characters, and at the old width it and the RT chips truncated each other to
  // uselessness. It is a flex sibling of the canvas, so the increase is kept to what the column
  // needs rather than rounded up.
  return (
    <div className="w-[640px] flex-shrink-0 flex flex-col border-l border-semantic-border-secondary bg-semantic-bg-primary overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-semantic-border-secondary px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-semibold text-semantic-text-primary">{neighborIp}</h3>
            <GenericTag
              label={status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
              variant={statusVariant}
              size="small"
            />
          </div>
          <p className="mt-0.5 text-xs text-semantic-text-secondary">
            AS {neighborInfo?.peerAs || '—'}
            {neighborInfo?.description ? ` · ${neighborInfo.description}` : ''} — {distinctPrefixes}{' '}
            {distinctPrefixes === 1 ? 'prefix' : 'prefixes'} announced
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close neighbor details"
          className="ml-2 shrink-0 rounded p-1 text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary"
        >
          <X size={16} />
        </button>
      </div>

      {/* Prefix table */}
      <div className="flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-semantic-text-secondary">No prefixes match the active filters</div>
        ) : (
          <table className="w-full border-collapse text-[11px]">
            <thead className="sticky top-0 bg-semantic-bg-secondary">
              <tr className="text-left text-semantic-text-secondary">
                <th className="px-2 py-1.5 font-semibold">Prefix</th>
                <th className="px-1.5 py-1.5 font-semibold">LP</th>
                <th className="px-1.5 py-1.5 font-semibold">MED</th>
                <th className="px-1.5 py-1.5 font-semibold">AS_PATH</th>
                <th className="whitespace-nowrap px-1.5 py-1.5 font-semibold">Next Hop</th>
                <th className="whitespace-nowrap px-1.5 py-1.5 font-semibold">{destinationHeader}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const row = group.representative;
                const dests = destinationsFor(group);
                return (
                  <tr
                    key={`${group.prefix}|${row.family}`}
                    onClick={() => handleRowClick(row)}
                    className={`border-b border-semantic-border-secondary transition-colors ${
                      onPrefixClick ? 'cursor-pointer hover:bg-semantic-state-active' : ''
                    }`}
                  >
                    <td className="whitespace-nowrap px-2 py-1.5">
                      <div className="flex flex-col leading-tight">
                        <span
                          className={
                            row.withdrawn
                              ? 'text-semantic-text-secondary line-through decoration-1'
                              : row.isBestPath
                              ? 'font-medium text-semantic-text-brand'
                              : 'text-semantic-text-primary'
                          }
                        >
                          {group.prefix}
                        </span>
                        {row.withdrawn && (
                          <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-semantic-text-danger">
                            Withdraw
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-1.5 py-1.5 tabular-nums text-semantic-text-primary">{row.localPref}</td>
                    <td className="px-1.5 py-1.5 tabular-nums text-semantic-text-primary">{row.med}</td>
                    <td className="max-w-[110px] truncate px-1.5 py-1.5 text-semantic-text-primary" title={row.asPath.join(' ')}>
                      {row.asPath.length ? row.asPath.join(' ') : '—'}
                    </td>
                    <td className="px-1.5 py-1.5 text-semantic-text-secondary">
                      <MultiChipCell values={nextHopsFor(group)} maxVisible={3} fillWidth />
                    </td>
                    <td className="px-1.5 py-1.5 text-semantic-text-secondary">
                      <MultiChipCell values={dests} maxVisible={3} fillWidth />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NeighborDetailPanel;
